import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import isPropValid from '@emotion/is-prop-valid';
import { ArrowRight } from '@sumup/icons';
import styled from '../../../../styles/styled';
import { focusVisible, disableVisually, cx, } from '../../../../styles/style-mixins';
import { useClickEvent } from '../../../../hooks/useClickEvent';
import { useComponents } from '../../../ComponentsContext';
import Body from '../../../Body';
import { Skeleton } from '../../../Skeleton';
const anchorStyles = ({ theme }) => css `
  position: relative;
  display: flex;
  align-items: center;
  height: 80px;
  width: 100%;
  background: none;
  border: none;
  outline: none;
  text-align: left;
  cursor: pointer;
  text-decoration: none;
  color: ${theme.colors.black};
  padding: ${theme.spacings.giga};
  transition: color ${theme.transitions.default},
    background-color ${theme.transitions.default};

  &:hover {
    background-color: ${theme.colors.n100};
  }

  &:active {
    background-color: ${theme.colors.n200};
  }

  &:disabled {
    ${disableVisually()};
  }

  ${theme.mq.untilGiga} {
    margin-bottom: ${theme.borderWidth.kilo};

    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 100%;
      right: ${theme.spacings.giga};
      left: ${theme.spacings.giga};
      width: calc(100% - 2 * ${theme.spacings.giga});
      border-bottom: ${theme.borderWidth.kilo} solid ${theme.colors.n300};
      transition: width ${theme.transitions.default},
        right ${theme.transitions.default}, left ${theme.transitions.default};
    }
  }

  ${theme.mq.giga} {
    height: 48px;
    width: 220px;
    padding: ${theme.spacings.kilo};
    margin-bottom: ${theme.spacings.kilo};
  }
`;
const anchorActiveStyles = ({ theme, isActive }) => isActive &&
    css `
    color: ${theme.colors.p500};

    &:hover {
      background-color: ${theme.colors.p100};
    }
  `;
const anchorOpenStyles = ({ theme, isOpen }) => isOpen &&
    css `
    ${theme.mq.untilGiga} {
      &::after {
        right: 0;
        left: 0;
        width: 100%;
      }
    }
  `;
const Anchor = styled('a', {
    shouldForwardProp: (prop) => isPropValid(prop),
})(focusVisible('inset'), anchorStyles, anchorActiveStyles, anchorOpenStyles);
const iconStyles = (theme) => css `
  position: relative;
  flex-shrink: 0;
  width: ${theme.iconSizes.mega};
  height: ${theme.iconSizes.mega};
  margin-right: ${theme.spacings.kilo};
`;
const iconWithBadgeStyles = (theme) => css `
  &::before {
    display: block;
    content: '';
    position: absolute;
    top: -8px;
    right: -8px;
    width: 10px;
    height: 10px;
    background-color: ${theme.colors.v500};
    border-radius: ${theme.borderRadius.circle};
  }
`;
const suffixStyles = (theme) => css `
  flex-shrink: 0;
  width: ${theme.iconSizes.kilo};
  height: ${theme.iconSizes.kilo};
  margin-left: auto;
  transition: transform ${theme.transitions.default};
`;
const externalIconStyles = css `
  transform: rotate(-45deg);
`;
const labelStyles = ({ theme }) => css `
  ${theme.mq.untilGiga} {
    font-size: ${theme.typography.headline.two.fontSize};
    line-height: ${theme.typography.headline.two.lineHeight};
  }
`;
const Label = styled(Body)(labelStyles);
export function PrimaryLink({ icon: Icon, label, onClick, isActive, isOpen, isExternal, suffix: Suffix, badge, tracking, secondaryGroups, ...props }) {
    const components = useComponents();
    const Link = components.Link;
    const handleClick = useClickEvent(onClick, tracking, 'primary-link');
    const suffix = Suffix && _jsx(Suffix, { css: suffixStyles, role: "presentation" }, void 0);
    const isExternalLink = isExternal || props.target === '_blank';
    return (_jsxs(Anchor, { ...props, onClick: handleClick, isActive: isActive, isOpen: isOpen, "aria-current": isActive ? 'page' : undefined, as: props.href ? Link : 'button', children: [_jsx(Skeleton, { css: cx(iconStyles, badge && iconWithBadgeStyles), children: _jsx(Icon, { role: "presentation", size: "24" }, void 0) }, void 0), _jsx(Skeleton, { children: _jsx(Label, { variant: isActive || isOpen ? 'highlight' : undefined, as: "span", noMargin: true, children: label }, void 0) }, void 0), isExternalLink && (_jsx(ArrowRight, { size: "16", role: "presentation", css: cx(suffixStyles, externalIconStyles) }, void 0)), suffix] }, void 0));
}
